import React, { createContext, useContext, useState, useCallback } from 'react';
import { articlesAPI } from '../services/api';

const FoldersContext = createContext();

export const useFolders = () => {
  const context = useContext(FoldersContext);
  if (!context) {
    throw new Error('useFolders must be used within FoldersProvider');
  }
  return context;
};

export const FoldersProvider = ({ children }) => {
  const [allArticles, setAllArticles] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);

  const fetchAllArticles = useCallback(async (force = false) => {
    // Cache for 30 seconds
    if (!force && allArticles && lastFetch && Date.now() - lastFetch < 30000) {
      return allArticles;
    }

    setLoading(true);
    try {
      const response = await articlesAPI.getMyArticles();
      const articles = response.data.articles;
      setAllArticles(articles);
      setLastFetch(Date.now());
      return articles;
    } catch (error) {
      console.error('Failed to fetch articles:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [allArticles, lastFetch]);

  const getFolders = useCallback(async (contentType = null) => {
    const articles = await fetchAllArticles();
    
    const folderMap = new Map();
    
    articles.forEach(article => {
      if (!article.title) return;
      
      // Skip placeholders completely
      if (article.body === 'Folder placeholder' || article.body === 'Subfolder placeholder') return;
      
      // Filter by contentType if specified
      if (contentType && article.contentType !== contentType) return;
      
      // Use FULL PATH as key to avoid merging different folders with same name
      const fullPath = article.title;
      const folderName = fullPath.includes(' / ') 
        ? fullPath.split(' / ').pop() 
        : fullPath;
      
      if (!folderMap.has(fullPath)) {
        folderMap.set(fullPath, {
          name: folderName,
          fullPath: fullPath,
          articleCount: 0,
          articles: []
        });
      }
      
      const folder = folderMap.get(fullPath);
      folder.articleCount++;
      folder.articles.push(article);
    });
    
    return Array.from(folderMap.values());
  }, [fetchAllArticles]);

  const getArticlesByFolder = useCallback(async (folderName, contentType = null) => {
    const articles = await fetchAllArticles();
    
    let filtered = articles.filter(a => a.title === folderName);
    
    if (contentType) {
      filtered = filtered.filter(a => a.contentType === contentType);
    }
    
    return filtered;
  }, [fetchAllArticles]);

  const invalidateCache = useCallback(() => {
    setAllArticles(null);
    setLastFetch(null);
  }, []);

  const value = {
    getFolders,
    getArticlesByFolder,
    invalidateCache,
    loading,
    fetchAllArticles
  };

  return (
    <FoldersContext.Provider value={value}>
      {children}
    </FoldersContext.Provider>
  );
};
